import { useEffect, useState, type CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Layers3,
  List,
  PanelsTopLeft,
  Percent,
  PieChart,
  Plus,
  Settings,
  SlidersHorizontal,
  Smartphone,
  Vault,
  X,
} from 'lucide-react';
import {
  CashbackBankFormPage,
  CashbackBankPage,
  CashbackPage,
  CashbackProgramPage,
} from '../features/cashback';
import {
  Navigate,
  NavLink,
  Link,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { createAccountApi } from '../api/client';
import type { Envelope } from '../api/contract';
import {
  AccountPage,
  ArchivePage,
  CategoryDetailsPage,
  CreateWorkspacePage,
  DashboardPage,
  EditLimitPage,
  JoinWorkspacePage,
  LimitsPage,
  OperationPage,
  ProfilePage,
  SessionsPage,
  StructureCreatePage,
  TransactionDetailsPage,
  WorkspaceDetailsPage,
  WorkspaceMembersPage,
  WorkspacesPage,
} from '../features/pages';
import {
  formatReportPeriodTitle,
  type ReportPeriod,
} from '../features/report-format';
import { ActivityPage, LegacyActivityRedirect } from '../features/activity';
import { ActivityStateProvider, useActivityState } from '../features/activity-state';
import { AppDataProvider, useAppData } from './AppContext';
import { DebtsPage, DebtCategoryPage, DebtCategoryFormPage, DebtMovementPage, DebtMovementFormPage } from '../features/debts';
import { LoginPage as EmailLoginPage } from '../features/auth/LoginPage';
import { safeReturnTo } from '../features/auth/returnTo';
import { EmailActionPage } from '../features/auth/EmailActionPage';
import { EmailRequestPage } from '../features/auth/EmailRequestPage';
import { SignInMethodsPage } from '../features/auth/SignInMethodsPage';
import { EmailLinkPage, YandexReauthPage } from '../features/auth/YandexReauthPage';
import { captureAuthEpoch } from './auth-context';
import { assertYandexIntent, clearYandexIntent, EMAIL_LINK_LOGIN, readYandexIntent } from '../features/auth/yandexIntent';
import { useYandexSignIn } from '../features/auth/useYandexSignIn';

export function App() {
  const mobile = useMobileViewport();
  const queryClient = useQueryClient();
  const location = useLocation();
  const explicitEmail = location.pathname === '/auth/email/login' || location.pathname === '/login';
  useEffect(() => {
    if (!mobile) queryClient.clear();
  }, [mobile, queryClient]);
  if (explicitEmail) return <EmailLoginPage />;
  if (location.pathname === EMAIL_LINK_LOGIN) return <EmailLinkLoginPage />;
  // The callback enforces the fixed recovery purpose on desktop. Neither
  // ordinary authentication route mounts the financial providers below.
  if (location.pathname === '/auth/complete') return <AuthCompletePage desktop={!mobile} />;
  if (location.pathname === '/auth/reauth/complete') return <YandexReauthPage />;
  if (location.pathname === '/auth/email/link') return <EmailLinkPage />;
  if (['/auth/email/register', '/auth/email/reset', '/auth/email/link'].includes(location.pathname)) {
    const action = location.pathname.endsWith('/link') || Boolean(location.hash) || new URLSearchParams(location.search).has('actionId');
    return action ? <EmailActionPage key={location.pathname} /> : <EmailRequestPage key={location.pathname} />;
  }
  if (!mobile) return <DesktopBlocker />;
  return (
      <div className="mobile-app" style={{ '--hairline': `${1 / Math.max(window.devicePixelRatio, 1)}px` } as CSSProperties}>
        <Routes>
          <Route
            element={
              <AppDataProvider>
                <ActivityStateProvider><AppLayout /></ActivityStateProvider>
              </AppDataProvider>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/activity" element={<ActivityPage />} />
            <Route path="/history" element={<LegacyActivityRedirect mode="history" />} />
            <Route path="/debts" element={<DebtsPage />} />
            <Route path="/debts/categories/new" element={<DebtCategoryFormPage />} />
            <Route path="/debts/categories/:id" element={<DebtCategoryPage />} />
            <Route path="/debts/categories/:id/edit" element={<DebtCategoryFormPage />} />
            <Route path="/debts/categories/:id/movements/new" element={<DebtMovementFormPage />} />
            <Route path="/debts/movements/:id" element={<DebtMovementPage />} />
            <Route path="/debts/movements/:id/edit" element={<DebtMovementFormPage />} />
            <Route path="/cashback" element={<CashbackPage />} />
            <Route path="/cashback/banks/new" element={<CashbackBankFormPage />} />
            <Route path="/cashback/banks/:bankId" element={<CashbackBankPage />} />
            <Route path="/cashback/banks/:bankId/edit" element={<CashbackBankFormPage />} />
            <Route path="/cashback/banks/:bankId/program" element={<CashbackProgramPage />} />
            <Route path="/operations/new" element={<OperationPage />} />
            <Route
              path="/operations/:operationId/edit"
              element={<OperationPage />}
            />
            <Route
              path="/transactions/:operationId"
              element={<TransactionDetailsPage />}
            />
            <Route path="/reports" element={<LegacyActivityRedirect mode="reports" />} />
            <Route path="/limits" element={<LimitsPage />} />
            <Route
              path="/limits/:categoryId/:month"
              element={<EditLimitPage />}
            />
            <Route path="/archive" element={<ArchivePage />} />
            <Route
              path="/account/limits"
              element={<Navigate to="/limits" replace />}
            />
            <Route
              path="/account/archive"
              element={<Navigate to="/archive" replace />}
            />
            <Route path="/account/profile" element={<ProfilePage />} />
            <Route path="/account/sessions" element={<SessionsPage />} />
            <Route path="/account/sign-in-methods" element={<SignInMethodsPage />} />
            <Route path="/account/workspaces" element={<WorkspacesPage />} />
            <Route
              path="/account/workspaces/new"
              element={<CreateWorkspacePage />}
            />
            <Route
              path="/account/workspaces/:workspaceId"
              element={<WorkspaceDetailsPage />}
            />
            <Route
              path="/account/workspaces/:workspaceId/members"
              element={<WorkspaceMembersPage />}
            />
            <Route
              path="/account/participants"
              element={<WorkspaceMembersPage />}
            />
            <Route path="/account/join" element={<JoinWorkspacePage />} />
            <Route path="/categories/new" element={<StructureCreatePage />} />
            <Route
              path="/categories/:categoryId/edit"
              element={<StructureCreatePage />}
            />
            <Route
              path="/categories/:categoryId"
              element={<CategoryDetailsPage />}
            />
            <Route
              path="/budgets/new"
              element={<StructureCreatePage forcedKind="budget" />}
            />
            <Route
              path="/budgets/:budgetId/edit"
              element={<StructureCreatePage forcedKind="budget" />}
            />
            <Route path="/account/*" element={<AccountPage />} />
            <Route path="/invite/:token" element={<JoinWorkspacePage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
  );
}

function useMobileViewport(): boolean {
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const update = () => setMobile(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return mobile;
}

const navigation = [
  { to: '/dashboard', label: 'Панель', icon: PanelsTopLeft },
  { to: '/debts', label: 'Долги', icon: List },
  { to: '/cashback', label: 'Кешбэк', icon: Percent },
  { to: '/activity', label: 'Отчёт', icon: PieChart },
  { to: '/account', label: 'Настройки', icon: Settings },
];

function AppLayout() {
  const activity = useActivityState();
  const { workspaces, workspace, setWorkspaceId } = useAppData();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [headerAnchor, setHeaderAnchor] = useState(() => new Date());
  const [dashboardTitle, setDashboardTitle] = useState('');
  const [cashbackMonth, setCashbackMonth] = useState('');
  const showWorkspaceSwitcher = searchParams.get('overlay') === 'workspace';
  const active = navigation.find(item => location.pathname.startsWith(item.to));
  const isDashboard = location.pathname.startsWith('/dashboard');
  const isReports = location.pathname.startsWith('/activity');
  const isDebts = location.pathname.startsWith('/debts');
  const isCashback = location.pathname.startsWith('/cashback');
  const isCashbackRoot = location.pathname === '/cashback';
  const isAccount = location.pathname.startsWith('/account');
  const reportPeriod = activity.period;
  const reportAnchor = activity.anchor;
  const monthTitle = new Intl.DateTimeFormat('ru-RU', {
    month: 'long',
    year: isDashboard ? undefined : 'numeric',
  }).format(headerAnchor);
  const sectionTitle = isReports
    ? formatReportPeriodTitle(reportAnchor, reportPeriod)
    : monthTitle;
  useEffect(() => {
    const updateDashboardTitle = (event: Event) => {
      setDashboardTitle(
        (event as CustomEvent<{ title: string }>).detail.title,
      );
    };
    window.addEventListener('kazna:dashboard-title', updateDashboardTitle);
    return () =>
      window.removeEventListener('kazna:dashboard-title', updateDashboardTitle);
  }, []);
  useEffect(() => {
    const updateCashbackMonth = (event: Event) => {
      setCashbackMonth((event as CustomEvent<{ month: string }>).detail.month);
    };
    window.addEventListener('kazna:cashback-month', updateCashbackMonth);
    return () => window.removeEventListener('kazna:cashback-month', updateCashbackMonth);
  }, []);
  const shiftSectionPeriod = (direction: -1 | 1) => {
    if (!isReports) {
      setHeaderAnchor(
        value =>
          new Date(
            value.getFullYear(),
            value.getMonth() + direction,
            1,
          ),
      );
    }
    window.dispatchEvent(
      new CustomEvent('kazna:period-shift', { detail: { direction } }),
    );
  };
  const openWorkspaceSwitcher = () => {
    const next = new URLSearchParams(searchParams);
    next.set('overlay', 'workspace');
    setSearchParams(next);
  };
  const closeWorkspaceSwitcher = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('overlay');
    setSearchParams(next, { replace: true });
  };
  const canRenderWithoutWorkspace =
    location.pathname.startsWith('/account') || location.pathname.startsWith('/invite/');
  return (
    <div className={`app-shell${isCashback && !isCashbackRoot ? ' no-tabbar' : ''}`}>
      <aside className="sidebar">
        <div className="brand">
          <img className="brand-mark" src="/icon.svg" alt="" aria-hidden="true" />
          <span>Казна</span>
        </div>
        <nav aria-label="Основная навигация">
          {navigation.map(item => (
            <NavLink key={item.to} to={item.to === '/activity' ? activity.path : item.to}>
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="app-body">
        {(!isCashback || isCashbackRoot) && <header className={`topbar${isReports ? ' activity-topbar' : ''}`}>
          {isCashback ? <span className="header-spacer" /> : isDashboard || isDebts ? (
            <button
              className="workspace-picker"
              aria-label="Выбрать пространство"
              onClick={openWorkspaceSwitcher}
            >
              <Layers3 size={20} />
            </button>
          ) : isAccount ? <span className="header-spacer" /> : (
            <button
              className="header-arrow"
              aria-label={
                isReports
                  ? 'Предыдущий период отчёта'
                  : 'Предыдущий период'
              }
              onClick={() => shiftSectionPeriod(-1)}
            >
              <ChevronLeft size={22} />
            </button>
          )}
          <div className="mobile-title">
            {isCashback ? (
              <><h1>Кешбэк</h1><p>{formatCashbackMonth(cashbackMonth)}</p></>
            ) : isDashboard ? (
              <button
                className="month-title-button"
                onClick={() => window.dispatchEvent(new Event('kazna:dashboard-period'))}
              >
                {dashboardTitle || monthTitle} <ChevronDown size={14} fill="currentColor" />
              </button>
            ) : isDebts ? <h1>Долги</h1> : isAccount ? <h1>Настройки</h1> : (
              <button
                className="section-period-button"
                aria-label={
                  isReports
                    ? `Настроить период отчёта, сейчас ${periodLabel(reportPeriod)}`
                    : undefined
                }
                onClick={() => window.dispatchEvent(new Event('kazna:section-period'))}
              >
                {sectionTitle}
                {isReports && (
                  <ChevronDown size={14} fill="currentColor" aria-hidden />
                )}
              </button>
            )}
            {!isDashboard && !isAccount && !isCashback && !isDebts && (
              <p>{isReports ? activity.mode === 'history' ? 'История' : 'Отчёты' : active?.label}</p>
            )}
          </div>
          {isCashback && (
            <Link className="header-action" aria-label="Добавить банк" to="/cashback/banks/new">
              <Plus size={25} />
            </Link>
          )}
          {isDashboard && (
            <button
              className="header-action"
              aria-label="Настройки панели"
              onClick={() => window.dispatchEvent(new Event('kazna:dashboard-settings'))}
            >
              <SlidersHorizontal size={19} />
            </button>
          )}
          {!isDashboard && !isAccount && !isCashback && !isDebts && (
            <button
              className="header-arrow"
              aria-label={
                isReports
                  ? 'Следующий период отчёта'
                  : 'Следующий период'
              }
              onClick={() => shiftSectionPeriod(1)}
            >
              <ChevronRight size={22} />
            </button>
          )}
          {isAccount && <span className="header-spacer" />}
          <label className="desktop-workspace-picker">
            <span className="sr-only">Пространство</span>
            <select
              value={workspace?.id ?? ''}
              onChange={event => setWorkspaceId(event.target.value)}
            >
              {workspaces.map(item => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
        </header>}
        <main className="content">
          {workspace === undefined && !canRenderWithoutWorkspace ? (
            <EmptyWorkspace />
          ) : (
            <div key={isDebts && location.pathname !== '/debts' ? 'debt-bound-context' : workspace?.id ?? 'no-workspace'}>
              <Outlet />
            </div>
          )}
        </main>
      </div>
      {(!isCashback || isCashbackRoot) && <nav className="bottom-nav" aria-label="Основная навигация">
        {navigation.map(item => (
          <NavLink key={item.to} to={item.to === '/activity' ? activity.path : item.to}>
            <item.icon size={20} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>}
      {showWorkspaceSwitcher && (
        <div
          className="dialog-backdrop sheet"
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) closeWorkspaceSwitcher();
          }}
        >
          <section
            className="dialog sheet workspace-switcher-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Пространства"
          >
            <header>
              <div>
                <h2>Пространства</h2>
                <p>Категории и операции не смешиваются</p>
              </div>
              <button
                type="button"
                aria-label="Закрыть"
                onClick={closeWorkspaceSwitcher}
              >
                <X size={23} />
              </button>
            </header>
            <div className="workspace-switcher-list">
              {workspaces.map(item => {
                const selected = item.id === workspace?.id;
                return (
                  <button
                    type="button"
                    key={item.id}
                    className={selected ? 'selected' : ''}
                    onClick={() => {
                      setWorkspaceId(item.id);
                      closeWorkspaceSwitcher();
                    }}
                  >
                    <span>
                      <strong>{item.name}</strong>
                      <small>Серверное пространство</small>
                    </span>
                    {selected && <Check size={22} strokeWidth={3} />}
                  </button>
                );
              })}
            </div>
            <Link
              className="workspace-create-link"
              to="/account/workspaces"
            >
              + Новое пространство
            </Link>
          </section>
        </div>
      )}
    </div>
  );
}

function periodLabel(period: ReportPeriod): string {
  if (period === 'DAY') return 'День';
  if (period === 'WEEK') return 'Неделя';
  if (period === 'YEAR') return 'Год';
  return 'Месяц';
}

function formatCashbackMonth(month: string): string {
  const value = /^\d{4}-\d{2}$/u.test(month)
    ? new Date(`${month}-01T12:00:00.000Z`)
    : new Date();
  return new Intl.DateTimeFormat('ru-RU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(value);
}

function DesktopBlocker() {
  return (
    <main className="desktop-blocker">
      <span className="desktop-phone"><Smartphone size={34} /></span>
      <h1>Казна Web работает на мобильных устройствах</h1>
      <p>Откройте <strong>app.kazna.space</strong> в Safari или Chrome на телефоне.</p>
      <img src="/qr-app.png" width="176" height="176" alt="QR-код для открытия Казны Web" />
      <a className="button button-primary" href="https://app.kazna.space">
        Открыть приложение
      </a>
    </main>
  );
}

function EmptyWorkspace() {
  return (
    <section className="empty-state">
      <Vault size={36} />
      <h2>Нет серверного пространства</h2>
      <p>Создайте первое пространство на вкладке «Аккаунт».</p>
    </section>
  );
}

function EmailLinkLoginPage() {
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const { start, starting, error } = useYandexSignIn({ returnTo, emailLinkRecovery: true });
  useEffect(() => {
    document.title = 'Вход для привязки почты — Казна';
  }, []);
  return (
    <main className="login-page public-auth-page email-action-page">
      <div className="login-card">
        <img className="brand-mark large" src="/icon.svg" alt="" aria-hidden="true" />
        <h1>Вход для привязки почты</h1>
        <p>Войдите в тот же аккаунт Яндекса, из которого запросили привязку почты.</p>
        <p>После входа заново откройте исходное письмо. Там вы подтвердите почту и аккаунт.</p>
        {error && <p role="alert" className="error">{error}</p>}
        <button className="button button-primary wide" disabled={starting} onClick={() => void start()}>
          {starting ? 'Открываем Яндекс ID…' : 'Войти через Яндекс ID'}
        </button>
      </div>
    </main>
  );
}

function AuthCompletePage({ desktop }: { desktop: boolean }) {
  const navigate = useNavigate();
  const [intent] = useState(readYandexIntent);
  const [api] = useState(() => createAccountApi(intent?.epoch ?? captureAuthEpoch(), () => { if (intent) assertYandexIntent(intent); }));
  const [error, setError] = useState(intent?.challengeId ? '' : 'Начатый вход не найден. Начните вход заново.');
  const [completed, setCompleted] = useState(false);
  const blocked = desktop && intent?.purpose !== 'email-link';
  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    if (blocked || !intent?.challengeId) return;
    const challengeId = intent.challengeId;
    const assertCurrent = () => { api.assertCurrent(); assertYandexIntent(intent); };
    const complete = async () => {
      for (let attempt = 0; attempt < 20 && !cancelled; attempt += 1) {
        assertCurrent();
        const status = await api.apiGet<Envelope<{ status: string }>>(
          `/auth/web/yandex/challenges/${challengeId}`,
        );
        if (cancelled) return;
        assertCurrent();
        if (status.data.status === 'CONFIRMED') {
          await api.apiMutation(`/auth/web/yandex/challenges/${challengeId}/exchange`, {
            scope: `web-auth:exchange:${challengeId}`,
          });
          if (cancelled) return;
          // This successful exchange intentionally rotates the epoch. Ownership
          // must still match; never clear or navigate using a newer intent.
          assertYandexIntent(intent);
          clearYandexIntent(intent);
          if (intent.purpose === 'email-link') setCompleted(true);
          else navigate(intent.returnTo, { replace: true });
          return;
        }
        if (['REJECTED', 'EXPIRED', 'EXCHANGED'].includes(status.data.status)) {
          throw new Error('Вход отклонён или истёк. Начните вход заново.');
        }
        await new Promise(resolve => { timer = window.setTimeout(resolve, 2_000); });
      }
      if (!cancelled) throw new Error('Подтверждение не завершено. Начните вход заново.');
    };
    void complete().catch(caught => {
      if (cancelled) return;
      clearYandexIntent(intent);
      setError(caught instanceof Error ? caught.message : 'Не удалось завершить вход');
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [api, blocked, intent, navigate]);
  if (blocked) return <DesktopBlocker />;
  if (completed) return <main className="login-page public-auth-page email-action-page"><section className="login-card">
    <h1>Теперь откройте письмо</h1>
    <p>Вход выполнен. Заново откройте исходную ссылку из письма, чтобы подтвердить почту и завершить привязку к тому же аккаунту.</p>
    <p>На странице письма потребуется повторное подтверждение через Яндекс ID.</p>
  </section></main>;
  if (error) return <main className="login-page public-auth-page email-action-page"><section className="login-card">
    <h1>Вход не завершён</h1><p role="alert" className="error">{error}</p>
    <Link className="button button-primary" to={intent?.purpose === 'email-link' ? EMAIL_LINK_LOGIN : `/login?mode=yandex&returnTo=${encodeURIComponent(intent?.returnTo ?? '/dashboard')}`}>Начать вход заново</Link>
  </section></main>;
  return (
    <main className="status-screen">
      <div className="spinner" />
      <h1>Завершаем вход…</h1>
    </main>
  );
}
